import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiCheck, FiFileText } from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import Input from '../../components/input';
import LogsComments from '../../components/LogsComments';
import TripMetaGrid from '../../components/TripMetaGrid';
import TripRouteJourney from '../../components/TripRouteJourney';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { canViewTripRouteJourney, hasTripPermission, isSuperAdminRole } from '../../utils/helper';
import { TripPermission } from '../../utils/app-constant';
import { errorNotify, successNotify } from '../../utils/toast';
import { apiService } from '../../services/apiService';
import { getTripDetails, setSelectedTripId } from '../../store/Slices/TripSlice';
import { fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import {
    buildRouteJourneyStops,
    buildTripMetaProps,
    buildTripMetrics,
    getFirstValue,
    getRealTripId,
    getTripCompletionSummary,
    getTripCustomerId,
    getTripIdDisplay,
    getRouteJourneySummary,
    getTripNotes,
} from './tripDetailView.helpers';
import '../../styles/TripCompleted.css';

const TripCompleted = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const realTripId = useMemo(() => getRealTripId(id), [id]);
    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { customerDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});
    const detailData = useMemo(() => tripDetailsData?.data || tripDetailsData || {}, [tripDetailsData]);
    const [tripDetailsRequestPending, setTripDetailsRequestPending] = useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = useState(null);
    const [fetchedCustomerName, setFetchedCustomerName] = useState('');
    const [customerLocations, setCustomerLocations] = useState([]);
    const [builtyLoading, setBuiltyLoading] = useState(false);
    const [podLoading, setPodLoading] = useState(false);

    const canViewBuilty = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.Builty, 'Builty', 'builty', 'bilty', 'Bilty', 'trip-builty']), []);
    const canViewPod = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.POD, 'POD', 'pod', 'Pod', 'trip-pod', 'Trip-POD']), []);

    useEffect(() => {
        let isActive = true;

        if (!realTripId) {
            setTripDetailsRequestPending(false);
            setLoadedTripDetailsId(null);
            return () => {
                isActive = false;
            };
        }

        setTripDetailsRequestPending(true);
        dispatch(getTripDetails(realTripId)).finally(() => {
            if (isActive) {
                setLoadedTripDetailsId(realTripId);
                setTripDetailsRequestPending(false);
            }
        });
        dispatch(setSelectedTripId(realTripId));
        dispatch(fetchCustomerDropdown());

        return () => {
            isActive = false;
        };
    }, [dispatch, realTripId]);

    useEffect(() => {
        let isActive = true;

        const fetchCustomerDetailData = async () => {
            const customerId = getTripCustomerId(detailData);
            setFetchedCustomerName('');
            setCustomerLocations([]);

            if (!customerId) return;

            try {
                const response = await apiService.getCustomerTripDetail(customerId);
                const data = response?.data || response || {};
                const customerName = getFirstValue(data, [
                    'name',
                    'label',
                    'title',
                    'customer_name',
                    'customerName',
                    'company_name',
                    'companyName',
                    'display_name',
                    'displayName',
                    'customer.name',
                    'customer.label',
                    'customer.customer_name',
                    'customer.company_name',
                    'data.name',
                    'data.label',
                    'data.customer_name',
                    'data.company_name',
                    'data.customer.name',
                    'data.customer.label',
                ]);

                if (isActive && customerName) {
                    setFetchedCustomerName(customerName);
                }

                const locations = data?.locations ||
                    data?.warehouses ||
                    data?.customer_locations ||
                    data?.customer_warehouses ||
                    [];
                const formattedLocations = (Array.isArray(locations) ? locations : []).map((location) => {
                    const locationName = location?.warehouse_name ||
                        location?.name ||
                        location?.label ||
                        location?.title ||
                        location?.location_name ||
                        location?.point_name ||
                        location?.warehouse?.warehouse_name ||
                        location?.warehouse?.name ||
                        location?.address ||
                        `Location #${location?.id || location?.value || ''}`;
                    const cityOrAddress = location?.city ||
                        location?.warehouse?.city ||
                        (location?.address && location.address !== locationName ? location.address : '');
                    const label = [locationName, cityOrAddress].filter(Boolean).join(' - ');

                    return {
                        value: String(location?.id || location?.value || location?.customer_warehouse_id || location?.warehouse_id || location?.warehouse?.id || ''),
                        id: location?.id,
                        customer_warehouse_id: location?.customer_warehouse_id,
                        warehouse_id: location?.warehouse_id || location?.warehouse?.id,
                        name: locationName,
                        label,
                        raw: location,
                    };
                });

                if (isActive) {
                    setCustomerLocations(formattedLocations);
                }
            } catch (error) {
                console.error('Error fetching customer trip detail:', error);
            }
        };

        fetchCustomerDetailData();

        return () => {
            isActive = false;
        };
    }, [detailData]);

    const tripIdDisplay = getTripIdDisplay(detailData, id);
    const dispatchTripId = realTripId || detailData?.id || id;
    const customerId = getTripCustomerId(detailData);
    const dropdownCustomerName = useMemo(() => {
        if (!customerId || !Array.isArray(customerDropdownData)) return '';

        const matchedCustomer = customerDropdownData.find((customer) =>
            String(customer?.value || customer?.id || customer?.customer_id) === String(customerId)
        );

        return matchedCustomer?.label ||
            matchedCustomer?.name ||
            matchedCustomer?.title ||
            matchedCustomer?.customer_name ||
            '';
    }, [customerDropdownData, customerId]);
    const metaProps = useMemo(() => {
        const resolvedMetaProps = buildTripMetaProps(detailData);
        return {
            ...resolvedMetaProps,
            customerName: fetchedCustomerName ||
                dropdownCustomerName ||
                (resolvedMetaProps.customerName === '--' && customerId ? `Customer #${customerId}` : resolvedMetaProps.customerName),
        };
    }, [customerId, detailData, dropdownCustomerName, fetchedCustomerName]);
    const routeStops = useMemo(() => buildRouteJourneyStops(detailData, customerLocations), [customerLocations, detailData]);
    const routeDistanceLabel = useMemo(() => getRouteJourneySummary(detailData, routeStops), [detailData, routeStops]);
    const canViewRouteJourney = useMemo(() => canViewTripRouteJourney(), []);
    const metrics = useMemo(() => buildTripMetrics(detailData), [detailData]);
    const completionSummary = useMemo(() => getTripCompletionSummary(detailData, metrics), [detailData, metrics]);
    const initialValues = useMemo(() => ({
        general_notes: getTripNotes(detailData),
    }), [detailData]);

    const isPageLoading = Boolean(
        realTripId && (tripDetailsLoading || tripDetailsRequestPending || loadedTripDetailsId !== realTripId)
    );

    const handleOpenBuilty = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setBuiltyLoading(true);
        try {
            const response = await apiService.getBilty(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Builty URL not found");
            }
        } catch (error) {
            console.error("Error generating builty:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate builty");
        } finally {
            setBuiltyLoading(false);
        }
    };

    const handleOpenPod = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setPodLoading(true);
        try {
            const response = await apiService.getPod(tripNumericId);
            const fileUrl = response?.data?.url || response?.url || (typeof response === "string" ? response : null);
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("POD URL not found");
            }
        } catch (error) {
            console.error("Error generating POD:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate POD");
        } finally {
            setPodLoading(false);
        }
    };

    const handleCompletedNext = () => {
        navigate(`/trips-list/trip-closing-activity/${dispatchTripId || tripIdDisplay}`);
    };

    if (isPageLoading) {
        return <SpinnerLoader />;
    }

    return (
        <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
            {({ values, handleBlur, setFieldValue }) => (
                <Form className="trip_completed_page">
                    <div className="desktop_header_only">
                        <Breadcrumbs values={["Home", "Trip Management", "Trip Detail", "Trip Completed"]} />
                        <div className="access_view transit_top_bar completed_top_bar">
                            <h2>Trip Completed</h2>
                            <div className="transit_top_actions">
                                {canViewBuilty && (
                                    <button
                                        type="button"
                                        className="btn_builty"
                                        onClick={handleOpenBuilty}
                                        disabled={builtyLoading}
                                        title="View Builty PDF"
                                    >
                                        {builtyLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiFileText style={{ fontSize: '14px' }} />
                                                <span>Builty</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                {canViewPod && (
                                    <button
                                        type="button"
                                        className="btn_builty"
                                        onClick={handleOpenPod}
                                        disabled={podLoading}
                                        title="View POD PDF"
                                    >
                                        {podLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiFileText style={{ fontSize: '14px' }} />
                                                <span>POD</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                <Button
                                    cancelBtn={() => navigate(-1)}
                                    cancelBtnText="Cancel"
                                    submitText="Next"
                                    type="button"
                                    shouldRow={false}
                                    isShowSubmitBtn={true}
                                    submitBtn={handleCompletedNext}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mobile_header_only mobile_completed_header">
                        <div className="mobile_header_left_controls">
                            <button className="mobile_menu_icon_btn" onClick={() => navigate(-1)} type="button" title="Back">
                                <FiArrowLeft style={{ fontSize: '20px' }} />
                            </button>
                            <h1 className="mobile_header_title">Trip Completed</h1>
                        </div>
                        <div className="mobile_header_right_controls">
                            <Button
                                submitText="Next"
                                type="button"
                                shouldRow={false}
                                isShowCancel={false}
                                isShowSubmitBtn={true}
                                submitBtn={handleCompletedNext}
                                buttonExtraCss="btn_figma_primary"
                            />
                        </div>
                    </div>

                    <Row className="completed_main_row g-3">
                        <Col lg={8} md={12} className="completed_left_col">
                            <div className="completed_card main_completed_card">
                                <div className="completed_card_header desktop_header_inside">
                                    <div>
                                        <h3 className="title">Trip Summary</h3>
                                        <p className="subtitle">Review the completed trip details before moving to closing activity.</p>
                                    </div>
                                    <div className="trip_id_green_pill">
                                        Trip ID: {tripIdDisplay}
                                    </div>
                                </div>

                                <div className="completed_card_header mobile_header_inside">
                                    <div>
                                        <h3 className="title">Trip Summary</h3>
                                        <p className="subtitle">Review completed trip details before closing.</p>
                                    </div>
                                    <div className="trip_id_green_pill">
                                        Trip ID: {tripIdDisplay}
                                    </div>
                                </div>

                                <TripMetaGrid {...metaProps} />

                                <div className="green_completed_success_banner">
                                    <div className="check_circle_icon">
                                        <FiCheck className="check_svg" />
                                    </div>
                                    <div className="completed_banner_text">
                                        <h4 className="banner_title">Trip completed</h4>
                                        <p className="banner_desc_desktop">
                                            {completionSummary.deliveredLabel} - Distance Covered {completionSummary.distanceLabel} - Closed At {completionSummary.closedAtLabel} - Total Spend {completionSummary.totalSpendLabel}
                                        </p>
                                        <div className="banner_desc_mobile">
                                            <p>{completionSummary.deliveredLabel}</p>
                                            <p>Distance Covered {completionSummary.distanceLabel}</p>
                                            <p>Closed At {completionSummary.closedAtLabel}</p>
                                            <p>Total Spend {completionSummary.totalSpendLabel}</p>
                                        </div>
                                    </div>
                                </div>

                                {canViewRouteJourney && (
                                    <TripRouteJourney
                                        stops={routeStops}
                                        distanceSummary={routeDistanceLabel}
                                    />
                                )}
                            </div>
                        </Col>

                        <Col lg={4} md={12} className="completed_right_col">
                            <div className="driver_details_sidebar service_cost_summary_details">
                                <LogsComments />
                            </div>

                            <div className="trip_notes_main_div mt-3">
                                <h5>General Notes</h5>
                                <div style={{ padding: '0 16px 16px 16px' }}>
                                    <Input
                                        label=""
                                        name="general_notes"
                                        placeholder="No notes added."
                                        quill={true}
                                        value={values.general_notes}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        required={false}
                                        disabled
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

export default TripCompleted;
